export default class CookiesManager {
  private destroyed = false
  private pgdid: string

  constructor() {
    this.pgdid = this.getOrCreatePgdid()
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }
    this.destroyed = true
  }

  // Helper function to generate UUID (Use crypto.randomUUID() if available)
  private generateUUID(): string {
    return crypto?.randomUUID ? crypto.randomUUID() : this.fallbackUUID()
  }

  // Fallback UUID generator if crypto.randomUUID() is not available
  private fallbackUUID(): string {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
    )
  }

  // Set a cookie (User ID or Session ID)
  public setCookie(name: string, value: string, options: { maxAge?: number } = {}): void {
    const cookieString = `${name}=${value}; Path=/; SameSite=Lax;${
      options.maxAge ? ` Max-Age=${options.maxAge};` : ''
    }`

    document.cookie = cookieString + (process.env.NODE_ENV === 'production' ? ' Secure;' : '')
  }

  // Set the pgdid cookie (400-day expiry)
  public setPgdidCookie(pgdid: string): void {
    this.setCookie('pgdid', pgdid, {
      maxAge: 400 * 24 * 60 * 60, // 400 days
    })
  }

  // Retrieve pgdid (persistent global device identifier) cookie
  public getPgdidCookie(): string | null {
    return this.getCookie('pgdid')
  }

  // Clear a cookie by name
  public clearCookie(name: string): void {
    document.cookie = `${name}=; Max-Age=0; Path=/;`
  }

  // Read or create the pgdid cookie. Returns the pgdid value.
  private getOrCreatePgdid(): string {
    const existing = this.getPgdidCookie()
    if (existing) {
      return existing
    }

    const pgdid = this.generateUUID()
    this.setPgdidCookie(pgdid)
    return pgdid
  }

  private getCookie(name: string): string | null {
    const cookies = document.cookie.split('; ')

    for (const cookie of cookies) {
      const [cookieName, value] = cookie.split('=')

      if (cookieName === name) {
        return value
      }
    }
    return null
  }
}
